import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Loader2,
  History,
  AlertCircle,
  User,
  Shield,
  RefreshCw,
  Filter,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAllAuditLogsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Filters
  const [filterClientId, setFilterClientId] = useState('');
  const [filterAction, setFilterAction] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [auditRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/campaign-audit`, { 
          headers: getAuthHeaders(),
          params: { limit: 500 }
        }),
        axios.get(`${API_URL}/api/admin/clients`, { headers: getAuthHeaders() })
      ]);
      setAuditLogs(auditRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filterClientId) params.client_id = filterClientId;
      if (filterAction) params.action = filterAction;
      
      const response = await axios.get(`${API_URL}/api/admin/campaign-audit`, {
        headers: getAuthHeaders(),
        params
      });
      setAuditLogs(response.data);
    } catch (error) {
      toast.error('Failed to filter logs');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterClientId('');
    setFilterAction('');
    fetchData();
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'created': return 'bg-green-100 text-green-800';
      case 'updated': return 'bg-blue-100 text-blue-800';
      case 'toggled': return 'bg-orange-100 text-orange-800';
      case 'state_change': return 'bg-purple-100 text-purple-800';
      case 'admin_override': return 'bg-red-100 text-red-800';
      case 'deleted': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get client name from ID
  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'Unknown';
  };

  if (loading && auditLogs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-all-audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/campaigns')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <History className="w-6 h-6" />
              All Campaign Audit Logs
            </h1>
            <p className="text-muted-foreground">Complete audit trail across all campaigns</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Client</label>
              <Select value={filterClientId || "all"} onValueChange={(v) => setFilterClientId(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Action Type</label>
              <Select value={filterAction || "all"} onValueChange={(v) => setFilterAction(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="created">Created</SelectItem>
                  <SelectItem value="updated">Updated</SelectItem>
                  <SelectItem value="toggled">Toggled</SelectItem>
                  <SelectItem value="state_change">State Change</SelectItem>
                  <SelectItem value="admin_override">Admin Override</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={applyFilters}>
              <Search className="w-4 h-4 mr-2" />
              Apply
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Logs</div>
            <div className="text-2xl font-bold">{auditLogs.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Created</div>
            <div className="text-2xl font-bold text-green-600">{auditLogs.filter(l => l.action === 'created').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Toggled</div>
            <div className="text-2xl font-bold text-orange-600">{auditLogs.filter(l => l.action === 'toggled').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">State Changes</div>
            <div className="text-2xl font-bold text-purple-600">{auditLogs.filter(l => l.action === 'state_change').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Admin Overrides</div>
            <div className="text-2xl font-bold text-red-600">{auditLogs.filter(l => l.action === 'admin_override').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">By Admin</div>
            <div className="text-2xl font-bold">{auditLogs.filter(l => l.changed_by?.type === 'admin').length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Audit Log Table */}
      <Card>
        <CardContent className="p-0">
          {auditLogs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No audit records found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">Timestamp</TableHead>
                  <TableHead className="w-[150px]">Client</TableHead>
                  <TableHead className="w-[120px]">Campaign</TableHead>
                  <TableHead className="w-[110px]">Action</TableHead>
                  <TableHead className="w-[180px]">Changed By</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id} className={log.action === 'admin_override' ? 'bg-orange-50' : ''}>
                    <TableCell className="font-mono text-xs">
                      {formatDate(log.timestamp)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {getClientName(log.client_id)}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-teal-600"
                        onClick={() => navigate(`/admin/campaigns/${log.campaign_id}/history`)}
                      >
                        {log.campaign_id?.slice(-8)}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Badge className={getActionBadgeColor(log.action)}>
                        {log.action.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {log.changed_by?.type === 'admin' ? (
                          <Shield className="w-4 h-4 text-orange-500" />
                        ) : (
                          <User className="w-4 h-4 text-blue-500" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{log.changed_by?.name || log.changed_by?.email || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{log.changed_by?.type}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.action === 'toggled' && `→ ${log.details?.toggled_to}`}
                      {log.action === 'state_change' && `${log.details?.total_states} states`}
                      {log.action === 'admin_override' && (
                        <span className="text-orange-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {log.details?.fields_changed?.join(', ')}
                        </span>
                      )}
                      {log.action === 'created' && 'New campaign'}
                      {log.action === 'updated' && 'Settings changed'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
